import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { LayoutDashboard, Globe, Settings, Radio, Home as HomeIcon, Menu, X, LogOut, Building2, Handshake, MessageSquare, Shield, Users, FileText, Activity, BarChart3 } from "lucide-react";
import Dashboard from "@/components/Dashboard";
import PortalManagement from "@/components/PortalManagement";
import SettingsPage from "@/components/SettingsPage";
import MapPage from "@/components/MapPage";
import AgencyTracker from "@/components/AgencyTracker";
import LoginPage from "@/components/LoginPage";
import ResetPasswordPage from "@/components/ResetPasswordPage";
import MessagesPage from "@/components/MessagesPage";
import SecurityPage from "@/components/SecurityPage";
import UserManagement from "@/components/UserManagement";
import TemplatePage from "@/components/TemplatePage";
import AgentActivityLog from "@/components/AgentActivityLog";
import StatisticsPage from "@/components/StatisticsPage";
import AntiCopy from "@/components/AntiCopy";
import { TooltipProvider } from "@/components/ui/tooltip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

const NAV_ITEMS = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/agenzie", label: "Agenzie", icon: Handshake },
  { path: "/messaggi", label: "Messaggi", icon: MessageSquare },
  { path: "/template", label: "Template", icon: FileText },
  { path: "/utenti", label: "Utenti", icon: Users, titolareOnly: true },
  { path: "/attivita", label: "Attività Agenti", icon: Activity, titolareOnly: true },
  { path: "/statistiche", label: "Statistiche", icon: BarChart3, titolareOnly: true },
  { path: "/sicurezza", label: "Sicurezza", icon: Shield, titolareOnly: true },
  { path: "/zone-residenziale", label: "Zone Residenziale", icon: HomeIcon, sector: "residenziale" },
  { path: "/zone-commerciale", label: "Zone Commerciale", icon: Building2, sector: "commerciale" },
  { path: "/portali", label: "Portali", icon: Globe, titolareOnly: true },
  { path: "/impostazioni", label: "Impostazioni", icon: Settings, titolareOnly: true },
];

function Sidebar({ mobileOpen, onClose, user, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [unseenCount, setUnseenCount] = useState(0);
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);
  const [segnalazioniCount, setSegnalazioniCount] = useState(0);
  const [cronEnabled, setCronEnabled] = useState(true);
  const [nextSearch, setNextSearch] = useState(null);
  const [todayAssignments, setTodayAssignments] = useState(null);

  // Fetch badge counts
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [unseen, msgs, segn] = await Promise.all([
          axios.get(`${API}/properties/unseen-count`).catch(() => ({ data: { count: 0 } })),
          axios.get(`${API}/messages/unread-count`).catch(() => ({ data: { count: 0 } })),
          user?.role === "titolare" ? axios.get(`${API}/segnalazioni/count`).catch(() => ({ data: { count: 0 } })) : { data: { count: 0 } },
        ]);
        setUnseenCount(unseen.data.count || 0);
        setUnreadMsgCount(msgs.data.count || 0);
        setSegnalazioniCount(segn.data.count || 0);
      } catch (e) { /* silent */ }
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Fetch next search time (admin only)
  useEffect(() => {
    if (user?.role !== "titolare") return;
    const fetchNext = () => {
      axios.get(`${API}/settings/next-search`).then(r => {
        setCronEnabled(r.data.enabled);
        setNextSearch(r.data.formatted || null);
      }).catch(() => {});
    };
    fetchNext();
    const interval = setInterval(fetchNext, 60000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (user?.role === "titolare") {
      const fetchCron = () => axios.get(`${API}/settings/cron`).then(r => setCronEnabled(r.data.enabled)).catch(() => {});
      fetchCron();
      const interval = setInterval(fetchCron, 15000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleNav = (path) => {
    if (path === "/dashboard") {
      // Mark properties as seen
      axios.post(`${API}/properties/mark-seen`).catch(() => {});
      setUnseenCount(0);
    }
    navigate(path);
    onClose();
  };

  const getBadge = (path) => {
    if (path === "/dashboard" && (unseenCount > 0 || segnalazioniCount > 0)) return unseenCount + segnalazioniCount;
    if (path === "/messaggi" && unreadMsgCount > 0) return unreadMsgCount;
    return 0;
  };

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={onClose} />
      )}
      <aside
        data-testid="sidebar"
        className={`fixed lg:sticky top-0 left-0 z-50 w-56 shrink-0 border-r border-zinc-200 bg-white flex flex-col h-screen transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo-radar.webp" alt="Property Radar" className="h-8 w-8 object-contain rounded-sm" />
            <div>
              <div className="flex items-center gap-1.5">
                <h1 className="font-heading text-base font-bold text-zinc-900 tracking-tight leading-none">Property Radar</h1>
                <span className="text-[8px] font-black text-amber-600 bg-amber-100 px-1 py-0.5 rounded-sm border border-amber-200 leading-none">BETA</span>
              </div>
              <p className="text-[10px] text-zinc-400 mt-0.5">SIACASA <span className="text-zinc-300">by Stefano Tronca</span></p>
            </div>
          </div>
          <button onClick={onClose} className="lg:hidden p-1 text-zinc-400 hover:text-zinc-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-0.5">
          {NAV_ITEMS
            .filter(item => {
              const userSectors = user?.sectors || [];
              // Agents see Dashboard + Messaggi + Template + zone per settore
              if (user?.role === "agente") {
                if (["/dashboard", "/messaggi", "/template"].includes(item.path)) return true;
                if (item.sector) return userSectors.includes(item.sector);
                return false;
              }
              // Assistente (Giorgia) sees Dashboard + Messaggi + Template + Agenzie + zone per settore
              if (user?.role === "assistente") {
                if (["/dashboard", "/messaggi", "/template", "/agenzie"].includes(item.path)) return true;
                if (item.sector) return userSectors.includes(item.sector);
                return false;
              }
              // Titolare-only items
              if (item.titolareOnly && user?.role !== "titolare") return false;
              return true;
            })
            .map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                onClick={() => handleNav(item.path)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-zinc-900 text-white"
                    : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                }`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
                {getBadge(item.path) > 0 && (
                  <span className="ml-auto text-[10px] font-bold bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
                    {getBadge(item.path) > 9 ? "9+" : getBadge(item.path)}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* User info + logout */}
        <div className="p-4 border-t border-zinc-200 space-y-3">
          {user && (
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-xs font-semibold text-zinc-700 truncate">{user.name}</div>
                <div className="text-[10px] text-zinc-400 truncate">{user.email}</div>
              </div>
              <button
                data-testid="logout-btn"
                onClick={onLogout}
                className="p-1.5 rounded-sm hover:bg-red-50 text-zinc-400 hover:text-red-600 transition-colors"
                title="Esci"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
          {user?.role === "titolare" && (
            <div>
              <div className="text-xs text-zinc-400 tracking-wider uppercase font-semibold">Prossima ricerca</div>
              <div className={`text-sm font-semibold mt-1 ${cronEnabled ? "text-zinc-700" : "text-red-500"}`}>
                {cronEnabled && nextSearch
                  ? nextSearch
                  : cronEnabled ? "In calcolo..." : "Disattivata"}
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

function AppContent() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [viewMode, setViewMode] = useState("titolare");
  
  // Set viewMode based on user role
  useEffect(() => {
    if (user) {
      if (user.role === "titolare") {
        setViewMode("titolare");
      } else if (user.role === "assistente") {
        setViewMode("Giorgia");
      } else if (user.role === "agente") {
        // Sub-agent sees parent's properties
        const agentName = user.parent_agent || user.name?.split(" ")[0] || user.email;
        setViewMode(agentName);
      }
    }
  }, [user]);
  const [todayAssignments, setTodayAssignments] = useState(null);
  const [filters, setFilters] = useState({
    zone: "", price_min: null, price_max: null, property_type: "", portal: "", status: "", agent: "", show_affitto: false
  });
  const [searchProgress, setSearchProgress] = useState(null);
  const [searchReport, setSearchReport] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Check auth on mount
  useEffect(() => {
    axios.get(`${API}/auth/me`)
      .then(res => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setAuthChecked(true));
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {}
    setUser(null);
    navigate("/");
  };

  const fetchStats = useCallback(async () => {
    const res = await axios.get(`${API}/stats`);
    setStats(res.data);
  }, []);

  const fetchProperties = useCallback(async (currentFilters) => {
    const params = {};
    const f = currentFilters || filters;
    if (f.zone) params.zone = f.zone;
    if (f.price_min) params.price_min = f.price_min;
    if (f.price_max) params.price_max = f.price_max;
    if (f.property_type) params.property_type = f.property_type;
    if (f.portal) params.portal = f.portal;
    if (f.status) params.status = f.status;
    try {
      const res = await axios.get(`${API}/properties`, { params: { ...params, limit: 1000 } });
      setProperties(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Errore caricamento proprietà:", e);
    }
  }, [filters]);

  const fetchPortals = useCallback(async () => {
    const res = await axios.get(`${API}/portals`);
    setPortals(res.data);
  }, []);

  const [comuni, setComuni] = useState([]);
  const fetchComuni = useCallback(async () => {
    const res = await axios.get(`${API}/comuni`);
    setComuni(res.data);
  }, []);

  const fetchTodayAssignments = useCallback(async () => {
    if (!user || user.role !== "titolare") return;
    const res = await axios.get(`${API}/today-assignments`);
    setTodayAssignments(res.data);
  }, [user]);

  const loadAll = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        fetchStats(),
        fetchProperties(),
        fetchPortals(),
        fetchComuni(),
        fetchTodayAssignments(),
      ]);
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          console.error(`loadAll[${i}] failed:`, r.reason);
        }
      });
    } catch (e) {
      console.error("Errore caricamento dati:", e);
    }
    setLoading(false);
  }, [fetchStats, fetchProperties, fetchPortals, user, fetchTodayAssignments]);

  useEffect(() => {
    if (user) loadAll();
  }, [user, loadAll]);

  // Redirect root/hub to dashboard
  useEffect(() => {
    if (user && (location.pathname === "/" || location.pathname === "/hub")) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, location.pathname, navigate]);

  const runSearch = async () => {
    setSearchLoading(true);
    setSearchProgress(null);
    try {
      const res = await axios.post(`${API}/search/run`);
      if (res.data.already_running) {
        toast.info("Ricerca già in corso — mostro il progresso.");
      } else {
        toast.info("Ricerca avviata...");
      }
      // Poll for progress + completion (works both for new and already-running searches)
      const pollInterval = setInterval(async () => {
        try {
          const status = await axios.get(`${API}/search/running`);
          if (status.data.progress) {
            setSearchProgress(status.data.progress);
          }
          if (!status.data.running) {
            clearInterval(pollInterval);
            setSearchLoading(false);
            setSearchProgress(null);
            try {
              const report = await axios.get(`${API}/search/report`);
              if (report.data) {
                const pr = report.data.portal_results || {};
                const totalPriv = Object.values(pr).reduce((s, v) => s + (typeof v === 'object' ? (v.privati || 0) : 0), 0);
                const totalAg = Object.values(pr).reduce((s, v) => s + (typeof v === 'object' ? (v.agenzie || 0) : 0), 0);
                const newRes = report.data.new_residential || 0;
                const newComm = report.data.new_commercial || 0;
                toast.success(
                  `Ricerca completata! ${newRes + newComm} nuovi annunci (${newRes} residenziali, ${newComm} commerciali). Trovati ${totalPriv} privati e ${totalAg} agenzie.`,
                  { duration: 8000 }
                );
                setSearchReport(report.data);
              }
            } catch (e) { console.debug("Report fetch skipped", e); }
            // Ricarica dati + secondo refresh con piccolo ritardo per sicurezza
            await loadAll();
            setTimeout(() => fetchStats(), 1500);
          }
        } catch {
          clearInterval(pollInterval);
          setSearchLoading(false);
          setSearchProgress(null);
        }
      }, 3000);
    } catch (e) {
      toast.error("Errore durante la ricerca");
      setSearchLoading(false);
      setSearchProgress(null);
    }
  };

  const updatePropertyStatus = async (propertyId, status) => {
    try {
      await axios.put(`${API}/properties/${propertyId}/status`, { status });
      setProperties(prev => prev.map(p => p.id === propertyId ? { ...p, status } : p));
      await fetchStats();
      toast.success("Stato aggiornato");
    } catch (e) {
      toast.error("Errore aggiornamento stato");
    }
  };

  const togglePortal = async (portalId) => {
    try {
      const res = await axios.put(`${API}/portals/${portalId}/toggle`);
      setPortals(prev => prev.map(p => p.id === portalId ? { ...p, active: res.data.active } : p));
      toast.success(res.data.active ? "Portale attivato" : "Portale disattivato");
    } catch (e) {
      toast.error("Errore toggle portale");
    }
  };

  const applyFilters = (newFilters) => {
    setFilters(newFilters);
    fetchProperties(newFilters);
  };

  // Auth loading
  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="w-6 h-6 border-2 border-zinc-600 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  // Reset password page — accessible without login
  if (location.pathname === "/reset-password") {
    return <ResetPasswordPage />;
  }

  // Not logged in → show login
  if (!user) {
    return <LoginPage onLogin={(data) => { handleLogin(data); navigate("/dashboard"); }} />;
  }

  // All pages = with sidebar
  return (
    <div className="flex min-h-screen bg-white">
      <Sidebar mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} user={user} onLogout={handleLogout} />
      <div className="fixed top-0 left-0 right-0 z-30 lg:hidden bg-white border-b border-zinc-200 px-4 py-3 flex items-center gap-3">
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setMobileOpen(true)}
          className="p-1.5 rounded-sm hover:bg-zinc-100"
        >
          <Menu className="w-5 h-5 text-zinc-700" />
        </button>
        <div className="flex items-center gap-2">
          <img src="/logo-radar.webp" alt="Property Radar" className="h-6 w-6 object-contain rounded-sm" />
          <span className="font-heading text-sm font-bold text-zinc-900">Property Radar</span>
        </div>
      </div>
      <main className="flex-1 min-w-0 pt-14 lg:pt-0 flex flex-col">
        <div className="flex-1">
          <Routes>
            <Route
              path="/dashboard"
              element={
                <Dashboard
                  stats={stats}
                  properties={properties}
                  portals={portals}
                  comuni={comuni}
                  loading={loading}
                  searchLoading={searchLoading}
                  searchProgress={searchProgress}
                  filters={filters}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  onRunSearch={runSearch}
                  onUpdateStatus={updatePropertyStatus}
                  onApplyFilters={applyFilters}
                  searchReport={searchReport}
                  onCloseReport={() => setSearchReport(null)}
                  user={user}
                  todayAssignments={todayAssignments}
                />
              }
            />
            <Route path="/agenzie" element={<AgencyTracker />} />
            <Route path="/messaggi" element={<MessagesPage user={user} />} />
            <Route path="/template" element={<TemplatePage user={user} />} />
            <Route path="/utenti" element={<UserManagement />} />
            <Route path="/attivita" element={<AgentActivityLog />} />
            <Route path="/statistiche" element={<StatisticsPage />} />
            <Route path="/sicurezza" element={<SecurityPage />} />
            <Route path="/zone-residenziale" element={<MapPage key="residenziale" category="residenziale" readOnly={user?.role !== "titolare"} />} />
            <Route path="/zone-commerciale" element={<MapPage key="commerciale" category="commerciale" readOnly={user?.role !== "titolare"} />} />
            <Route
              path="/portali"
              element={<PortalManagement portals={portals} onToggle={togglePortal} />}
            />
            <Route path="/impostazioni" element={<SettingsPage />} />
          </Routes>
        </div>
        <footer className="border-t border-zinc-100 bg-white px-8 py-3">
          <p className="text-[11px] text-zinc-400 text-center">
            © 2026 Siacasa Immobiliare Srl. P.IVA 06553840965. Tutti i diritti riservati.
          </p>
        </footer>
      </main>
    </div>
  );
}

function App() {
  return (
    <TooltipProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AntiCopy />
        <AppContent />
      </BrowserRouter>
    </TooltipProvider>
  );
}

export default App;
